import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  cancelarImpressao, listarImpressoes, reenfileirarImpressao, saudeDaFila,
} from '../lib/consultas';
import { dataHoraBR } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// Tela IMPRESSÕES — o controle da fila (19/08/2026).
//
// POR QUE ELA EXISTE, em uma frase: a loja passou 12 dias sem imprimir e
// ninguém tinha como saber. O agente (o programa que roda no computador da
// loja e fala com a impressora) tinha sido fechado, e os documentos foram
// empilhando numa fila que só o banco enxergava.
//
// Então esta tela mostra, antes de qualquer lista, a pergunta que importa:
// *o agente está vivo?* O sinal é documento parado com ZERO tentativa — se
// ele estivesse rodando, teria pegado o trabalho e, no pior caso, registrado
// o erro. Zero tentativa depois de 10 minutos quer dizer que ninguém pegou.

const COR: Record<string, string> = {
  fila: 'border-alerta/60 text-alerta',
  enviado: 'border-ok/60 text-ok',
  falha: 'border-erro/60 text-erro',
  cancelado: 'border-edge text-dim',
};

const ROTULO_TIPO: Record<string, string> = {
  a_ver: 'Vale "a ver"',
  orcamento: 'Orçamento',
};

export default function Impressoes() {
  const cliente = useQueryClient();
  const [verTudo, setVerTudo] = useState(false);

  const saude = useQuery({
    queryKey: ['impressoes', 'saude'],
    queryFn: saudeDaFila,
    refetchInterval: 30_000,
  });
  const jobs = useQuery({
    queryKey: ['impressoes', 'lista'],
    queryFn: () => listarImpressoes(50),
    refetchInterval: 30_000,
  });

  const recarregar = () => cliente.invalidateQueries({ queryKey: ['impressoes'] });
  const reenviar = useMutation({ mutationFn: reenfileirarImpressao, onSuccess: recarregar });
  const cancelar = useMutation({ mutationFn: cancelarImpressao, onSuccess: recarregar });

  const s = saude.data;
  // Sem "ver tudo", a tela mostra o que ainda está em jogo — o histórico
  // inteiro é ruído no dia a dia.
  const lista = (jobs.data ?? []).filter((j) => verTudo || j.status !== 'cancelado');

  return (
    <Layout titulo="Impressões">
      <div className="space-y-4">
        {/* O AGENTE ESTÁ VIVO? É a primeira pergunta da tela, de propósito. */}
        {s && s.parados_ha_10min > 0 && (
          <Aviso tipo="erro">
            <strong>{s.parados_ha_10min} documento(s) parado(s) há mais de 10 minutos
            sem ninguém pegar.</strong> Isso quase sempre quer dizer que o
            programa de impressão não está rodando no computador da loja —
            abra o <span className="font-mono">iniciar.bat</span> na pasta
            <span className="font-mono"> C:\serginho-impressora</span> (ou reinicie
            o computador: ele sobe sozinho ao entrar no Windows).
          </Aviso>
        )}

        <section className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="cartao p-3">
            <p className="rotulo">Na fila</p>
            <p className="text-2xl font-black tabular-nums">{s?.na_fila ?? '—'}</p>
          </div>
          <div className="cartao p-3">
            <p className="rotulo">Falharam</p>
            <p className={`text-2xl font-black tabular-nums ${s && s.falhas > 0 ? 'text-erro' : ''}`}>
              {s?.falhas ?? '—'}
            </p>
          </div>
          <div className="cartao p-3">
            <p className="rotulo">Último papel</p>
            <p className="text-sm font-bold">
              {s?.ultimo_enviado_em ? dataHoraBR(s.ultimo_enviado_em) : 'nenhum ainda'}
            </p>
          </div>
        </section>

        {jobs.error ? <Aviso tipo="erro">{mensagemDeErro(jobs.error)}</Aviso> : null}
        {reenviar.error ? <Aviso tipo="erro">{mensagemDeErro(reenviar.error)}</Aviso> : null}
        {cancelar.error ? <Aviso tipo="erro">{mensagemDeErro(cancelar.error)}</Aviso> : null}
        {jobs.isLoading && <Carregando texto="Lendo a fila" />}

        <section className="cartao p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="rotulo">Documentos ({lista.length})</h2>
            <label className="flex items-center gap-2 text-xs text-mute">
              <input type="checkbox" className="h-4 w-4" checked={verTudo}
                     onChange={(e) => setVerTudo(e.target.checked)} />
              mostrar cancelados
            </label>
          </div>

          {lista.length === 0 && !jobs.isLoading && (
            <p className="mt-2 text-sm text-dim">Nada na fila.</p>
          )}

          <ul className="mt-2 divide-y divide-edge">
            {lista.map((j) => (
              <li key={j.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="font-semibold leading-snug">{j.titulo}</p>
                  <p className="text-xs text-dim">
                    {ROTULO_TIPO[j.tipo] ?? j.tipo}
                    {' · '}{dataHoraBR(j.created_at)}
                    {j.solicitante_nome && ` · ${j.solicitante_nome}`}
                    {j.tentativas > 0 && ` · ${j.tentativas} tentativa(s)`}
                  </p>
                  {j.erro && <p className="mt-0.5 text-xs text-erro">{j.erro}</p>}
                  {/* O que não tem tentativa nenhuma e está esperando é o
                      sintoma do agente fora do ar — dito na linha, não só no
                      alerta do topo. */}
                  {j.status === 'fila' && j.tentativas === 0 && (
                    <p className="mt-0.5 text-xs text-alerta">aguardando o computador da loja pegar</p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <span className={`border px-1.5 py-0.5 font-mono text-[0.625rem] uppercase ${
                    COR[j.status] ?? 'border-edge text-dim'}`}>
                    {j.status}
                  </span>
                  {/* Reimprimir serve para o que falhou E para 2ª via do que
                      já saiu (o documento fica guardado na linha). */}
                  <button type="button" className="text-xs text-dim underline"
                          disabled={reenviar.isPending}
                          onClick={() => reenviar.mutate(j.id)}>
                    {j.status === 'enviado' ? '2ª via' : 'mandar de novo'}
                  </button>
                  {j.status !== 'enviado' && j.status !== 'cancelado' && (
                    <button type="button" className="text-xs text-erro underline"
                            disabled={cancelar.isPending}
                            onClick={() => cancelar.mutate(j.id)}>
                      cancelar
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <p className="text-xs text-dim">
          O papel sai pelo computador da loja, que consulta esta fila a cada 15
          segundos. Documento que falha três vezes para de tentar e fica como
          <strong> falha</strong> — o motivo aparece na linha.
        </p>
      </div>
    </Layout>
  );
}
